import axios, {
  isAxiosError,
  type AxiosInstance,
  type AxiosProgressEvent,
  type AxiosResponse,
} from 'axios';

import { HttpClientError } from '@errors/httpClient.error';
import { UnknownError } from '@errors/unknown.error';

import { interceptors } from './interceptors';

class BackendHttpClient {
  private readonly instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: import.meta.env.VITE_BACKEND_URL,
      timeout: 100000,
    });
    interceptors.refresh(this.instance);
    interceptors.setToken(this.instance);
  }

  public async get<T = unknown, TParams = unknown>({
    headers = {},
    params,
    path,
  }: {
    headers?: Record<string, string>;
    params?: TParams;
    path: string;
  }): Promise<AxiosResponse<T>> {
    try {
      const response = await this.instance.get<T>(path, {
        params,
        headers: {
          ...headers,
        },
      });
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public async post<T, TBody = unknown>({
    body,
    onUploadProgress,
    path,
    timeout,
  }: {
    body?: TBody;
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
    path: string;
    timeout?: number;
  }): Promise<AxiosResponse<T>> {
    try {
      // sem timeout, vale o padrão da instância
      const response = await this.instance.post<T>(path, body, {
        onUploadProgress,
        ...(timeout ? { timeout } : {}),
      });
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public async put<T, TBody = unknown, TParams = unknown>({
    body,
    params,
    path,
    timeout,
  }: {
    body?: TBody;
    params?: TParams;
    path: string;
    timeout?: number;
  }): Promise<AxiosResponse<T>> {
    try {
      const response = await this.instance.put<T>(path, body, {
        params,
        ...(timeout ? { timeout } : {}),
      });
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public async patch<T, TBody = unknown, TParams = unknown>({
    body,
    params,
    path,
  }: {
    body?: TBody;
    params?: TParams;
    path: string;
  }): Promise<AxiosResponse<T>> {
    try {
      const response = await this.instance.patch<T>(path, body, {
        params,
      });
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public async delete<T, TParams = unknown>({
    params,
    path,
  }: {
    params?: TParams;
    path: string;
  }): Promise<AxiosResponse<T>> {
    try {
      const response = await this.instance.delete<T>(path, {
        params,
      });
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public removeBearerToken(): void {
    this.instance.defaults.headers.common.Authorization = '';
  }

  public setBearerToken(token: string): void {
    this.instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  private throwError(err: unknown): never {
    if (isAxiosError(err)) {
      throw new HttpClientError({
        message: `${err.response?.status} - ${err.response?.statusText}`,
        response: err.response,
      });
    }

    throw new UnknownError({
      context: {
        originalError: err,
      },
    });
  }
}

export default new BackendHttpClient();
